/**
 * Aufrufe des Ticketmoduls.
 *
 * Die Typen kommen aus dem erzeugten OpenAPI-Schema und werden hier nur
 * benannt. Ein von Hand geschriebener Typ waere eine zweite Wahrheit ueber
 * die Schnittstelle -- und die zweite Wahrheit ist irgendwann die falsche.
 */

import type { components, operations } from '../../api/schema';
import { download, request } from '../../lib/apiClient';

export type Ticket = components['schemas']['TicketRead'];
export type TicketListItem = components['schemas']['TicketListItem'];
export type TicketList = components['schemas']['TicketList'];
export type TicketCreate = components['schemas']['TicketCreate'];
export type TicketUpdate = components['schemas']['TicketUpdate'];
export type StateCategory = components['schemas']['TicketStateCategory'];
export type TicketLockInfo = components['schemas']['TicketLockInfo'];
export type HistoryEntry = components['schemas']['HistoryEntry'];
export type HistoryChange = components['schemas']['HistoryChange'];
export type Board = components['schemas']['BoardRead'];
export type BoardColumn = components['schemas']['BoardColumn'];
export type DuplicateCandidate = components['schemas']['DuplicateCandidate'];
export type BulkUpdateRequest = components['schemas']['BulkUpdateRequest'];
export type BulkUpdateResult = components['schemas']['BulkUpdateResult'];
export type TicketLookupItem = components['schemas']['TicketLookupItem'];

export type TicketListQuery = NonNullable<operations['listTickets']['parameters']['query']>;

export function listTickets(query: TicketListQuery): Promise<TicketList> {
  return request<TicketList>('/tickets', { query });
}

export function getTicket(ticketId: string): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}`);
}

export type TicketFilterCounts = components['schemas']['TicketFilterCounts'];
export type TicketFilterCountQuery = NonNullable<
  operations['countTicketsByFilter']['parameters']['query']
>;

/** Die Zahlen ueber den Reitern der Ticketliste -- je Reiter und Schalter. */
export function getTicketFilterCounts(query: TicketFilterCountQuery): Promise<TicketFilterCounts> {
  return request<TicketFilterCounts>('/tickets/filter-counts', { query });
}

export type TicketTabCounts = components['schemas']['TicketTabCounts'];

/** Die Zahlen an den Reitern -- F1-55. */
export function getTicketTabCounts(ticketId: string): Promise<TicketTabCounts> {
  return request<TicketTabCounts>(`/tickets/${ticketId}/counts`);
}

export function createTicket(payload: TicketCreate): Promise<Ticket> {
  return request<Ticket>('/tickets', { method: 'POST', body: payload });
}

/**
 * Teilaktualisierung mit Nebenlaeufigkeitspruefung.
 *
 * `version` wird als `If-Match` gesendet (docs/05, Abschnitt 5). Ohne diesen
 * Kopf koennte die Aenderung eine fremde ueberschreiben, ohne dass es jemand
 * bemerkt -- der klassische verlorene Schreibvorgang.
 */
export function updateTicket(
  ticketId: string,
  payload: TicketUpdate,
  version: number,
): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}`, {
    method: 'PATCH',
    body: payload,
    headers: { 'If-Match': `"${String(version)}"` },
  });
}

export type TicketAction = 'claim' | 'resolve' | 'close' | 'reopen' | 'dismiss-waiting';

/**
 * Zustandsuebergang als benannte Aktion.
 *
 * docs/05, Abschnitt 2: Ein Ticket schliesst man nicht ueber eine
 * Feldaenderung. Am Uebergang haengen Vorbedingungen, Protokoll und -- ab
 * Phase 4 -- die SLA-Uhr.
 *
 * `claim` ist der einzige Uebergang, der zugleich die Zuweisung aendert: Wer
 * uebernimmt, ist danach Bearbeiter und das Ticket steht auf "In
 * Bearbeitung". Auch das gehoert deshalb hierher und nicht in ein
 * Auswahlfeld.
 */
export function performTicketAction(ticketId: string, action: TicketAction): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}/actions/${action}`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Tags, Beobachtung, Sperre
// ---------------------------------------------------------------------------

export function setTicketTags(ticketId: string, tagIds: string[]): Promise<string[]> {
  return request<string[]>(`/tickets/${ticketId}/tags`, {
    method: 'POST',
    body: { tag_ids: tagIds },
  });
}

export function setTicketWatching(ticketId: string, watching: boolean): Promise<string[]> {
  return request<string[]>(`/tickets/${ticketId}/watch`, {
    method: 'POST',
    body: { watching },
  });
}

/**
 * Holt die weiche Sperre (F1-24, K-21).
 *
 * "Weich" heisst: Sie haelt niemanden ab, sie warnt nur. Die eigentliche
 * Absicherung gegen den verlorenen Schreibvorgang ist `If-Match` beim
 * Speichern -- die Sperre erspart nur, dass zwei Leute erst nach zehn
 * Minuten Arbeit voneinander erfahren.
 */
export function acquireTicketLock(ticketId: string): Promise<TicketLockInfo> {
  return request<TicketLockInfo>(`/tickets/${ticketId}/lock`, { method: 'POST' });
}

export function releaseTicketLock(ticketId: string): Promise<undefined> {
  return request<undefined>(`/tickets/${ticketId}/lock`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Historie, Ausdruck, Kanban, Duplikate
// ---------------------------------------------------------------------------

export function getTicketHistory(ticketId: string): Promise<HistoryEntry[]> {
  return request<HistoryEntry[]>(`/tickets/${ticketId}/history`);
}

export function downloadTicketPdf(ticketId: string, includeInternal: boolean) {
  return download(`/tickets/${ticketId}/pdf?include_internal=${String(includeInternal)}`);
}

export function getBoard(query: {
  queue_id?: string;
  owner_id?: string;
  customer_id?: string;
}): Promise<Board> {
  return request<Board>('/tickets/board', { query });
}

export function findDuplicates(title: string): Promise<DuplicateCandidate[]> {
  return request<DuplicateCandidate[]>('/tickets/duplicates', { query: { title } });
}

// ---------------------------------------------------------------------------
// Zusammenfuehren, Aufteilen, Massenbearbeitung
// ---------------------------------------------------------------------------

/** Teiltreffer auf Nummer und Betreff -- fuer Nachschlagefelder. */
export function lookupTickets(q: string): Promise<TicketLookupItem[]> {
  return request<TicketLookupItem[]>('/tickets/lookup', { query: { q } });
}

export function mergeTicket(ticketId: string, targetTicketId: string): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}/actions/merge`, {
    method: 'POST',
    body: { target_ticket_id: targetTicketId },
  });
}

export function splitTicket(
  ticketId: string,
  payload: { title: string; article_ids: string[] },
): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}/actions/split`, { method: 'POST', body: payload });
}

export function bulkUpdateTickets(payload: BulkUpdateRequest): Promise<BulkUpdateResult> {
  return request<BulkUpdateResult>('/tickets/actions/bulk-update', {
    method: 'POST',
    body: payload,
  });
}

/**
 * Eltern-Kind-Beziehung -- F1-16, K-11.
 *
 * ``null`` loest die Zuordnung. Der Server weist Kreise ab: Ein Ticket, das
 * ueber Umwege sein eigener Elternvorgang waere, liesse sich nie mehr
 * schliessen.
 */
export function setTicketParent(ticketId: string, parentTicketId: string | null): Promise<Ticket> {
  return request<Ticket>(`/tickets/${ticketId}/parent`, {
    method: 'POST',
    body: { parent_ticket_id: parentTicketId },
  });
}
